import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Paper,
  Alert,
  useTheme,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ModalMAIQ from "app/components/ModalMAIQ";
import { Resource } from '../../types';
import { useResource } from 'app/context/ResourceContext';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';

interface AddResourceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddResourceModal: React.FC<AddResourceModalProps> = ({ open, onClose, onSuccess }) => {
  const { addResource } = useResource();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const theme = useTheme();
  const [formData, setFormData] = useState<Partial<Resource>>({
    name: '',
    path: '',
    component: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    
    // Special handling for path field to add '/' prefix if needed
    if (name === 'path' && typeof value === 'string') {
      const pathValue = value.startsWith('/') ? value : `/${value}`;
      setFormData(prev => ({
        ...prev,
        [name]: pathValue,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name as string]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Use the context function instead of direct API call
      await addResource(formData);
      onSuccess();
      onClose();
      // Reset form data
      setFormData({
        name: '',
        path: '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalMAIQ 
      open={open} 
      onClose={onClose} 
      hideDefaultCloseButton={true}
    >
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddCircleOutlineIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                Add New Resource
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Resource Name *
            </Typography>
            <TextField
              fullWidth
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter resource name"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Path *
            </Typography>
            <TextField
              fullWidth
              name="path"
              value={formData.path}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter path (/ will be added automatically)"
              inputProps={{
                sx: { py: 1.5 }
              }}
              helperText="Forward slash (/) will be added automatically if not provided"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Component *
            </Typography>
            <TextField
              fullWidth
              name="component"
              value={formData.component}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter component name"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>
        </Box>

        {/* Footer with actions - fixed */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2
          }}
        >
          <ButtonMAIQ
            variant="outlined"
            color="primary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </ButtonMAIQ>
          <Tooltip title="Create a new resource">
            <ButtonMAIQ
              variant="contained"
              type="submit"
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            >
              {isSubmitting ? 'Creating...' : 'Create Resource'}
            </ButtonMAIQ>
          </Tooltip>
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

export default AddResourceModal; 